// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    isLive: boolean;
    /** Whether the server actually has this socket in a room. The pill says what is true, not what
     *  was asked for — a connection that never came up must not read as live. */
    isWatching: boolean;
    onToggleLive: () => void;
    /** `null` once a choice has not been made yet, so the button is not shown before it is asked for. */
    isSoundOn: boolean | null;
    onToggleSound: () => void;
    /** How many orders arrived that the list has not been told about. `null` means "we cannot know". */
    newCount: number | null;
    /** Whether any card on screen went out of the current filters after it changed. */
    hasChanged: boolean;
    onRefresh: () => void;
    onDismiss: () => void;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * What live orders look like when nobody asked for the list to move.
 *
 * A new order never enters the list on its own: a list that grows and re-sorts itself under a
 * counter who is reading it is worse than a list that is a minute old. So arrivals are counted here
 * and the list stays exactly as it was opened until this says otherwise.
 *
 * It sits away from the actions bar on purpose. The bar is where the counter works; this is a thing
 * that interrupts, and an interruption that lands on top of the controls gets clicked by accident.
 * On a phone it rides above the navigation bar for the same reason.
 *
 * The switch is the way out of being interrupted at all — a manager with full access who is watching
 * the shop rather than working the counter turns it off and the rooms are left entirely. It names
 * both of its states, because a single word with a dot beside it reads as a label, not as a control.
 */
export default function OrdersLive({
    isLive,
    isWatching,
    onToggleLive,
    isSoundOn,
    onToggleSound,
    newCount,
    hasChanged,
    onRefresh,
    onDismiss,
}: Props) {
    const t = useTranslation();

    const hasNews = newCount === null || newCount > 0 || hasChanged;

    const headline =
        newCount === null
            ? t.orders.newOrdersUnknown
            : newCount === 1
              ? t.orders.newOrdersOne
              : newCount > 1
                ? t.orders.newOrdersMany.replace("{count}", String(newCount))
                : t.orders.changedNote;

    return (
        <div className="orders-live">
            <div className="orders-live-controls">
                <button
                    type="button"
                    className="orders-live-switch"
                    data-on={isLive}
                    onClick={onToggleLive}
                    role="switch"
                    aria-checked={isLive}
                    title={isLive ? t.orders.liveOnHint : t.orders.liveOffHint}
                >
                    <span className="orders-live-track">
                        <span className="orders-live-knob" data-beating={isLive && isWatching} />
                    </span>
                    <span className="orders-live-switch-label">{isLive ? t.orders.liveLabel : t.orders.liveOffLabel}</span>
                </button>

                {/* Only after the question has been answered — before that, the popup is the control. */}
                {isSoundOn !== null && (
                    <button
                        type="button"
                        className="orders-live-sound"
                        data-on={isSoundOn}
                        onClick={onToggleSound}
                        aria-pressed={isSoundOn}
                        title={isSoundOn ? t.orders.soundOnHint : t.orders.soundOffHint}
                    >
                        <img src="/icons/notifications-icon.svg" alt="" width={16} height={16} />
                    </button>
                )}
            </div>
            {hasNews && (
                <div className="orders-live-snack" role="status">
                    <div className="orders-live-snack-text">
                        <p className="orders-live-snack-title">{headline}</p>
                        {/* Said separately, because a changed order and a new one are two different
                            reasons to refresh and the counter may care about only one of them. */}
                        {hasChanged && newCount !== 0 && <p className="orders-live-snack-note">{t.orders.changedNote}</p>}
                    </div>

                    <button type="button" className="orders-live-refresh" onClick={onRefresh}>
                        <img src="/icons/refresh-icon.svg" alt="" width={18} height={18} />
                        {t.orders.refreshLabel}
                    </button>

                    <button
                        type="button"
                        className="orders-live-dismiss"
                        onClick={onDismiss}
                        aria-label={t.orders.dismissLabel}
                        title={t.orders.dismissLabel}
                    >
                        ×
                    </button>
                </div>
            )}
        </div>
    );
}
